import useLocalStorage from "@/hooks/useLocalStorage";
import useUsers from "@/hooks/useUsers";
import AuthenticationService from "@/services/api/AuthenticationService";
import { MASKS } from "@/utils/constants/masks";
import { logger } from "@/utils/functions/logger";
import { Delete } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  ClickAwayListener,
  IconButton,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { zip } from "gzip-js";
import { ChangeEvent, FC, useCallback, useEffect, useMemo } from "react";
import QRCode from "react-qr-code";

interface QRCodePopupProps {
  onClose: () => void;
}

const QRCodePopup: FC<QRCodePopupProps> = ({ onClose }) => {
  const [userId, setUserId] = useLocalStorage("SERVER_USER_ID", "");
  const [alias, setAlias] = useLocalStorage("SERVER_ALIAS", "");
  const [token, setToken] = useLocalStorage("SERVER_TOKEN", "");
  const [ip, setIp] = useLocalStorage("SERVER_HOST", "");
  const [port, setPort] = useLocalStorage("SERVER_PORT", "");
  const [maskMAC, setMaskMAC] = useLocalStorage("MASK_MAC", "");
  const [grammar, setGrammar] = useLocalStorage("GRAMMAR", "");
  const [grammarFileName, setGrammarFileName] = useLocalStorage("GRAMMAR_FILE_NAME", "");
  const { users, getUsers } = useUsers();

  useEffect(() => {
    getUsers();
  }, []);

  const onUserSelect = (user: User) => {
    setAlias(user.alias ?? "");
    AuthenticationService.getMqttCredentials(user.id)
      .then((creds) => {
        setUserId(String(creds.userId));
        setToken(creds.sessionToken);
        setIp(creds.host);
        setPort(String(creds.port));
      })
      .catch(logger.error);
  };

  useEffect(() => {
    if (users.length > 0) {
      const currentUser = users.find((u) => String(u.id) === userId) ?? users[0];
      onUserSelect(currentUser);
    }
  }, [users]);

  const onUserChange = (e: SelectChangeEvent) => {
    const user = users.find((u) => String(u.id) === e.target.value);
    if (user) onUserSelect(user);
  };

  const onRegenerateCredentials = () => {
    const currentUser = users.find((u) => String(u.id) === userId);
    if (!currentUser) return;
    AuthenticationService.revokeMqttCredentials(currentUser.id)
      .then(() => onUserSelect(currentUser))
      .catch(logger.error);
  };
  const onAliasChange = (e: ChangeEvent<HTMLInputElement>) => {
    setAlias(e.target.value);
  };
  const onIpChange = (e: ChangeEvent<HTMLInputElement>) => {
    setIp(e.target.value);
  };
  const onPortChange = (e: ChangeEvent<HTMLInputElement>) => setPort(e.target.value);
  const onMaskMACChange = (e: SelectChangeEvent) => setMaskMAC(e.target.value);

  const onClickAway = (e: MouseEvent | TouchEvent) => {
    // If the target is the body, it means the clicked element is the select
    if (e.target != document.body) {
      onClose();
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const [file] = event.target.files;
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target && e.target.result) {
          try {
            const content: string = e.target.result as string;
            setGrammar(JSON.stringify(JSON.parse(content)));
            setGrammarFileName(file.name);
          } catch (e) {
            logger.error(e);
          }
        }
      };
      reader.readAsText(file);
    }
  };

  const searchFileInput = () => {
    const inputElement = document.createElement("input");
    inputElement.hidden;
    inputElement.type = "file";
    inputElement.accept = "*.json";
    inputElement.addEventListener(
      "change",
      (e: Event) => handleFileChange(e as unknown as ChangeEvent<HTMLInputElement>) // forced cast
    );
    inputElement.click();
  };

  // Use callback prevent qrcode rerender
  const getQRCodeValue = useCallback(
    (userId: string, alias: string, token: string, ip: string, port: string, maskMAC: string, grammar: string) => {
      return btoa(
        zip(
          JSON.stringify({
            userId: userId,
            mqttSessionToken: token,
            mqttHost: ip,
            mqttPort: port,
            userAlias: alias,
            maskMAC: maskMAC,
            grammar,
          })
        )
          .map((n) => String.fromCharCode(n))
          .join("")
      );
    },
    [users.map((user) => user.id).join(";"), userId, alias, token, ip, port, maskMAC, grammar]
  );

  const qrCodeComponent = useMemo(
    () => <QRCode size={400} value={getQRCodeValue(userId, alias, token, ip, port, maskMAC, grammar)} />,
    [users.map((user) => user.id).join(";"), userId, alias, token, ip, port, maskMAC, grammar]
  );

  const handleRemoveGrammarFile = () => {
    setGrammar("");
    setGrammarFileName("");
  };

  return (
    <ClickAwayListener onClickAway={onClickAway}>
      <Paper
        elevation={5}
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "700px",
          padding: "10px",
        }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: "10px" }}>
          <Typography sx={{ fontSize: "1.4em" }}>Générer un QRCode</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          {qrCodeComponent}
          <Stack direction="column" justifyContent="space-between" spacing={1}>
            <Select placeholder="Utilisateur" onChange={onUserChange} value={userId}>
              {users.map((user) => (
                <MenuItem key={user.id} value={String(user.id)}>
                  {user.username}
                </MenuItem>
              ))}
            </Select>
            <TextField variant="outlined" label="Alias" onChange={onAliasChange} value={alias} />
            <TextField variant="outlined" label="Host du serveur" onChange={onIpChange} value={ip} />
            <TextField variant="outlined" label="Port du serveur" onChange={onPortChange} value={port} />
            <TextField
              variant="outlined"
              label="Adresse du masque"
              onChange={(e) => setMaskMAC(e.target.value)}
              value={maskMAC}
            />
            <Select placeholder="Masque" onChange={onMaskMACChange} value={maskMAC}>
              {Object.keys(MASKS).map((maskName) => (
                <MenuItem key={maskName} value={MASKS[maskName]}>
                  {maskName}
                </MenuItem>
              ))}
            </Select>
            {grammar ? (
              <Stack width="100%" justifyContent="space-between" direction="row" alignItems="center">
                <Tooltip title={grammarFileName}>
                  <Typography noWrap sx={{ maxWidth: 200 }}>
                    {grammarFileName}
                  </Typography>
                </Tooltip>
                <IconButton onClick={handleRemoveGrammarFile}>
                  <Delete />
                </IconButton>
              </Stack>
            ) : null}
            <Button variant="contained" onClick={searchFileInput}>
              Grammaire
            </Button>
            <Button variant="outlined" onClick={onRegenerateCredentials}>
              Régénérer le token
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </ClickAwayListener>
  );
};

export default QRCodePopup;
